import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Recycle, Shield, Users, Rocket, ArrowRight, TrendingUp } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const FOCUS_AREAS = [
  {
    id: 'A1',
    icon: Recycle,
    color: '#8DC21F',
    gradient: 'linear-gradient(135deg, rgba(141,194,31,0.15), rgba(141,194,31,0.05))',
    challengeCount: 4,
    titleES: 'Nuevas cadenas de valor para residuos',
    titleEN: 'New Value Chains for Waste',
    descES: 'Transformar residuos en recursos. Crear productos comerciales y mercados para outputs de planta. Posicionarnos en flujos emergentes (baterías, PVs, palas) y abordar retos energéticos estratégicos (CO₂, biometano).',
    descEN: 'Transform waste into resources. Create commercial products and markets for plant outputs. Position ourselves in emerging streams (batteries, PVs, blades) and address strategic energy challenges (CO₂, biomethane).',
    impactES: 'Convertir costes en ingresos. Nuevas líneas de negocio. Liderazgo en economía circular.',
    impactEN: 'Turn costs into revenue. New business lines. Circular economy leadership.',
  },
  {
    id: 'A2',
    icon: Shield,
    color: '#3b82f6',
    gradient: 'linear-gradient(135deg, rgba(59,130,246,0.15), rgba(59,130,246,0.05))',
    challengeCount: 2,
    titleES: 'Tecnología aplicada a operaciones y seguridad',
    titleEN: 'Technology for Operations & Safety',
    descES: 'Proteger a nuestras personas mediante tecnología. Reducir riesgos físicos críticos (pinchazos, caídas, gases, seguridad vial). Optimizar flota con sensorización integral y gestor común para maximizar eficiencia operativa.',
    descEN: 'Protect our people through technology. Reduce critical physical risks (punctures, falls, gases, road safety). Optimize fleet with comprehensive sensorization and common manager to maximize operational efficiency.',
    impactES: 'Reducción de accidentes. Diferenciador en licitaciones. Eficiencia operativa transversal.',
    impactEN: 'Accident reduction. Tender differentiator. Cross-cutting operational efficiency.',
  },
  {
    id: 'A3',
    icon: Users,
    color: '#f59e0b',
    gradient: 'linear-gradient(135deg, rgba(245,158,11,0.15), rgba(245,158,11,0.05))',
    challengeCount: 1,
    titleES: 'Cultura y personas: el motor de la innovación',
    titleEN: 'Culture & People: Innovation Engine',
    descES: 'Cerrar la brecha digital en +12.000 operativos. Sin adopción tecnológica por parte de las personas, no hay innovación real. Transformar la cultura digital para que la organización esté preparada para el futuro.',
    descEN: 'Close digital gap in +12,000 operational workers. Without technological adoption by people, there is no real innovation. Transform digital culture so the organization is ready for the future.',
    impactES: 'Base para adopción de cualquier innovación. Capacitación masiva. Cultura digital.',
    impactEN: 'Foundation for any innovation adoption. Massive upskilling. Digital culture.',
  },
  {
    id: 'A4',
    icon: Rocket,
    color: '#8b5cf6',
    gradient: 'linear-gradient(135deg, rgba(139,92,246,0.15), rgba(139,92,246,0.05))',
    challengeCount: 1,
    titleES: 'Robustez y madurez de la función de innovación',
    titleEN: 'Innovation Function Robustness & Maturity',
    descES: 'Fortalecer la capacidad interna de innovación. Desarrollar inteligencia de mercado sistemática para anticipar tendencias y necesidades del cliente antes que la competencia. Ganar propuestas con visión de futuro.',
    descEN: 'Strengthen internal innovation capability. Develop systematic market intelligence to anticipate trends and customer needs before competition. Win proposals with future vision.',
    impactES: 'Anticipar al cliente. Mayor competitividad. Función de innovación escalable y visible.',
    impactEN: 'Anticipate customers. Higher competitiveness. Scalable and visible innovation function.',
  },
];

function FocusAreaCard({ area, index, inView, lang }) {
  const Icon = area.icon;
  const title = lang === 'es' ? area.titleES : area.titleEN;
  const desc = lang === 'es' ? area.descES : area.descEN;
  const impact = lang === 'es' ? area.impactES : area.impactEN;

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: index * 0.15, ease: [0.16, 1, 0.3, 1] }}
      className="group relative"
      style={{
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '20px',
        padding: '2rem',
        backdropFilter: 'blur(12px)',
        overflow: 'hidden',
        transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
      }}
    >
      {/* Gradient background on hover */}
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{ background: area.gradient }}
      />

      {/* Content */}
      <div className="relative z-10">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div 
            className="flex-shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
            style={{
              background: `${area.color}15`,
              border: `1px solid ${area.color}30`,
            }}
          >
            <Icon size={24} style={{ color: area.color }} />
          </div>

          {/* Challenge count badge */}
          <div 
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold"
            style={{
              background: `${area.color}10`,
              border: `1px solid ${area.color}30`,
              color: area.color,
            }}
          >
            <TrendingUp size={14} />
            <span>{area.challengeCount} {lang === 'es' ? 'retos' : 'challenges'}</span>
          </div>
        </div>

        {/* Area ID */}
        <div className="mb-3">
          <span 
            className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-md"
            style={{
              background: `${area.color}15`,
              color: area.color,
            }}
          >
            {area.id}
          </span>
        </div>

        {/* Title */}
        <h3 
          className="text-xl font-semibold mb-4 leading-tight transition-colors duration-300"
          style={{ color: 'rgba(255,255,255,0.95)' }}
        >
          {title}
        </h3>

        {/* Description */}
        <p className="text-sm text-white/60 leading-relaxed mb-4">
          {desc}
        </p>

        {/* Impact */}
        <div 
          className="flex items-start gap-2 p-3 rounded-xl"
          style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.05)',
          }}
        >
          <ArrowRight size={16} className="flex-shrink-0 mt-0.5" style={{ color: area.color }} />
          <p className="text-xs font-medium" style={{ color: area.color }}>
            {impact}
          </p>
        </div>
      </div>

      {/* Hover border effect */}
      <div 
        className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{ 
          border: `1px solid ${area.color}40`,
        }}
      />
    </motion.div>
  );
}

export default function FocusAreasSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const { lang } = useLanguage();

  return (
    <section
      ref={ref}
      className="relative py-20 sm:py-32 overflow-hidden bg-black dark:bg-black"
      style={{
        background: 'linear-gradient(180deg, #000 0%, rgba(8,3,8,0.98) 50%, #000 100%)',
      }}
    >
      {/* Background glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ 
          background: 'radial-gradient(ellipse 60% 40% at 50% 50%, rgba(139,92,246,0.04) 0%, transparent 70%)',
        }}
      />

      <div className="v-container relative z-10">
        {/* Tag */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="mb-4"
        >
          <span className="v-tag" style={{ 
            background: 'rgba(139,92,246,0.1)', 
            color: '#8b5cf6',
            border: '1px solid rgba(139,92,246,0.25)' 
          }}>
            {lang === 'es' ? 'ESTRATEGIA 2026' : 'STRATEGY 2026'}
          </span>
        </motion.div>

        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.1 }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-white leading-[1.1] tracking-tight">
              {lang === 'es' ? (
                <>
                  4 <em style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', color: 'rgba(255,255,255,0.5)' }}>áreas de foco</em>{' '}
                  que concentran{' '}
                  <span style={{ color: '#8b5cf6' }}>nuestro futuro</span>
                </>
              ) : (
                <>
                  4 <em style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', color: 'rgba(255,255,255,0.5)' }}>focus areas</em>{' '}
                  concentrating{' '}
                  <span style={{ color: '#8b5cf6' }}>our future</span>
                </>
              )}
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex flex-col justify-center"
          >
            <p className="text-base text-white/60 leading-relaxed mb-4">
              {lang === 'es'
                ? 'Definidas estratégicamente por el Comité de Innovación. Cada área agrupa retos específicos con impacto directo en negocio, operaciones, seguridad y posicionamiento competitivo.'
                : 'Strategically defined by the Innovation Committee. Each area groups specific challenges with direct impact on business, operations, safety and competitive positioning.'}
            </p>
            <p className="text-sm text-white/50 leading-relaxed">
              {lang === 'es'
                ? 'Desde transformar residuos en valor hasta capacitar digitalmente a +12.000 operativos, estas áreas representan nuestra hoja de ruta hacia una organización más innovadora, segura y competitiva.'
                : 'From transforming waste into value to digitally upskilling +12,000 operational workers, these areas represent our roadmap towards a more innovative, safe and competitive organization.'}
            </p>
          </motion.div>
        </div>

        {/* Focus Areas Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {FOCUS_AREAS.map((area, index) => (
            <FocusAreaCard
              key={area.id}
              area={area}
              index={index}
              inView={inView}
              lang={lang}
            />
          ))}
        </div>

        {/* Stats footer */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.6 }}
          className="mt-12 pt-8 border-t border-white/5"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold text-white mb-1">4</div>
              <div className="text-xs text-white/50 uppercase tracking-wider">
                {lang === 'es' ? 'Áreas estratégicas' : 'Strategic areas'}
              </div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white mb-1">8</div>
              <div className="text-xs text-white/50 uppercase tracking-wider">
                {lang === 'es' ? 'Retos prioritarios' : 'Priority challenges'}
              </div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white mb-1">+40</div>
              <div className="text-xs text-white/50 uppercase tracking-wider">
                {lang === 'es' ? 'Participantes workshops' : 'Workshop participants'}
              </div>
            </div>
            <div>
              <div className="text-3xl font-bold" style={{ color: '#8b5cf6' }}>H2 2026</div>
              <div className="text-xs text-white/50 uppercase tracking-wider">
                {lang === 'es' ? 'Activación' : 'Activation'}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
